import React, { useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import * as Yup from 'yup';

import { FormServiceContext } from 'app/utils/contexts';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';

import { setSelectedVehicles, clearSelectedVehicles } from 'app/store/slices/vehicle';

import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@material-ui/core';

import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import AssignmentIcon from '@material-ui/icons/Assignment';

import notification from 'app/config/notification';
import helpers from 'app/utils/helpers';
import VehicleForm from 'app/components/VehicleForm';

import { GenericDrawer } from 'app/components/GenericDrawer/GenericDrawer';
import { clearFiles } from 'app/store/slices/forms';
import { preparedFormData, validateDocumentsDescription } from 'app/utils/forms/functions';
import { verifyLicencaNameAndFormat } from 'app/utils/forms/services/licenses';
import { DOCUMENT_TYPES } from 'app/utils/constants';
import filesEndpoint from 'app/api/services/filesEndpoint';
import { cancelDAE, daeIsPaid } from 'app/store/slices/daes';
import { STATUS_CODES } from 'app/config/status';
import licenseEndpoint from 'app/api/services/licenseEndpoint';
import { EXTERNAL_CONTEXT } from 'app/config/urls';
import { hideLoading, showLoading } from 'app/store/slices/settings';
import { updateRequestDetails } from 'app/store/slices/requests';
import { SERVICES_CODES } from 'app/config/services';

const DialogVehicleSelection = (props) => {
    const { open, handleCancel, loading, handleSave, selectedVehicle, inspectedVehicles, data } = props;

    const validationSchema = Yup.object().shape({
        cdVeiculo: Yup.string().required('Campo obrigatório'),
    });

    const filteredInspectedVehicles = useMemo(() => {
        const usedPlates = data.map((v) => v.veiculo.dePlaca);
        return inspectedVehicles.filter((v) => !usedPlates.includes(v.dePlaca));
    }, [data, inspectedVehicles]);

    const onCancel = (e, reason) => {
        if (reason && reason === 'backdropClick') return;
        if (handleCancel) handleCancel(e);
    };

    return (
        <Dialog disableEscapeKeyDown open={open} onClose={onCancel}>
            <DialogTitle>Selecione o veículo</DialogTitle>
            <Formik
                enableReinitialize={true}
                initialValues={{ cdVeiculo: '' }}
                validationSchema={validationSchema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={() => {}}>
                {(formProps) => {
                    const { errors } = formProps;
                    return (
                        <Form>
                            <DialogContent style={{ paddingTop: '0px', maxWidth: '350px' }}>
                                <FormControl
                                    variant="filled"
                                    margin="dense"
                                    fullWidth
                                    error={!!errors.cdVeiculo}
                                    style={{ minWidth: '300px' }}>
                                    <Field name="cdVeiculo" id="cdVeiculo" native as={Select}>
                                        <option aria-label="None" value="">
                                            Selecione
                                        </option>
                                        {filteredInspectedVehicles.map((c, index) => {
                                            return (
                                                <option key={`type-${index}`} value={c.cdVeiculo}>
                                                    {c.dePlaca}
                                                </option>
                                            );
                                        })}
                                    </Field>
                                    {errors.cdVeiculo ? <FormHelperText>{errors.cdVeiculo}</FormHelperText> : null}
                                </FormControl>
                            </DialogContent>
                            {!loading && (
                                <DialogActions>
                                    <Button onClick={() => handleCancel()} color="primary">
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            formProps.validateForm().then((res) => {
                                                if (Object.keys(res).length <= 0) {
                                                    handleSave(formProps.values, selectedVehicle);
                                                }
                                            });
                                        }}
                                        color="primary">
                                        Salvar
                                    </Button>
                                </DialogActions>
                            )}
                            {loading && (
                                <Grid item xs={12} style={{ textAlign: 'center', marginBottom: '5px' }}>
                                    <CircularProgress size={12} />
                                </Grid>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

function VehicleTableAdd(props) {
    const dispatch = useDispatch();
    const { classes, data, setData, intl, oldVehicles, hideDeleteButton, hideAddButton } = props;
    const {
        isInternalForm,
        canEdit,
        onCancel: onCancelRequest,
        errorsList,
        setErrorsList,
    } = useContext(FormServiceContext);

    const [open, setOpen] = useState(false);
    const [vehicleToAdd, setVehicleToAdd] = useState(null);
    const [loading, setLoading] = useState(false);
    const [maxVehicles, setMaxVehicles] = useState(0);

    const [openDrawer, setOpenDrawer] = useState(false);
    const [vehicleSelected, setVehicleSelected] = useState({});
    const [filteredCollectionData, setFilteredCollectionData] = useState({
        dataBoExcluido: [],
        dataBoNotExcluido: [],
    });

    const inspectedVehicles = useSelector((state) => state.vehicle.inspectedVehicles);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const vehicleCategories = useSelector((state) => state.vehicle.vehicleCategories);
    const otherDocuments = useSelector((state) => state.forms.files);

    const request = useSelector((state) => state.requests.details);
    const service = useSelector((state) => state.services.details);

    const showDeleteButton = useMemo(() => {
        if (isInternalForm || hideDeleteButton) return false;
        if (request.cdSituacao === STATUS_CODES.AGUARDANDO_PAGAMENTO) {
            return true;
        }
        return canEdit;
    }, [canEdit, hideDeleteButton, isInternalForm, request.cdSituacao]);

    useEffect(() => {
        if (service.boGeraDae) {
            if (
                filteredCollectionData.dataBoNotExcluido &&
                request.cdSituacao > STATUS_CODES.EM_ANALISE &&
                filteredCollectionData.dataBoNotExcluido.length > 0
            ) {
                if (maxVehicles === 0) setMaxVehicles(filteredCollectionData.dataBoNotExcluido.length);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredCollectionData]);

    useEffect(() => {
        (() => {
            const temp = _.cloneDeep(data);
            const dataBoExcluido = temp.filter((c) => c.boExcluido);
            const dataBoNotExcluido = temp.filter((c) => !c.boExcluido);
            setFilteredCollectionData({
                dataBoExcluido,
                dataBoNotExcluido,
            });
            dispatch(setSelectedVehicles([...dataBoNotExcluido]));
        })();
    }, [data, dispatch]);

    const changeElement = async (index) => {
        let temp = [...data];
        if (index === undefined) {
            handleClickOpen(temp.length);
        } else {
            if (window.confirm('Deseja excluir este item?')) {
                dispatch(clearSelectedVehicles(temp[index]));
                dispatch(showLoading());

                if (request.cdSolicitacao && request.cdSituacao > STATUS_CODES.EM_CONSTRUCAO) {
                    try {
                        const isPaid = await daeIsPaid(request.cdSolicitacao, temp[index].veiculo?.dePlaca, intl);
                        if (isPaid) {
                            setLoading(false);
                            dispatch(hideLoading());
                            return;
                        }
                    } catch (error) {
                        dispatch(hideLoading());
                    }

                    if (request.cdSituacao === STATUS_CODES.AGUARDANDO_PAGAMENTO && temp.length === 1) {
                        if (window.confirm('Ao excluir este item, a solicitação será cancelada. Deseja continuar?')) {
                            onCancelRequest();
                            dispatch(hideLoading());
                        } else {
                            notification(
                                intl.formatMessage({
                                    id: 'MESSAGES.SERVICE.FORMS.DELETE_VEHICLE_NOT_ALLOWED_EMPTY',
                                }),
                                {
                                    toastId: 'MESSAGES.SERVICE.FORMS.DELETE_VEHICLE_NOT_ALLOWED_EMPTY',
                                    type: 'error',
                                },
                            );
                        }
                        setLoading(false);
                        dispatch(hideLoading());
                        return;
                    }

                    const cdVeiculo = temp[index]?.cdVeiculo;
                    if (cdVeiculo && request?.jsonSolicitacao) {
                        let json = JSON.parse(request.jsonSolicitacao);
                        if (json.licencaVeiculos && json.licencaVeiculos.length > 0) {
                            const found = _.find(json.licencaVeiculos, ['cdVeiculo', cdVeiculo]);
                            let cdLicencaVeiculo = null;
                            if (found) {
                                cdLicencaVeiculo = found.cdLicencaVeiculo;
                            }

                            try {
                                if (cdLicencaVeiculo) {
                                    const canceled = await cancelDAE(request.cdSolicitacao, [temp[index]]);
                                    if (!canceled) {
                                        throw String(
                                            'Problema ao cancelar DAE gerada para este veículo. Tente novamente.',
                                        );
                                    }
                                    const response = await licenseEndpoint.deleteVehicle(
                                        cdLicencaVeiculo,
                                        request.cdSolicitacao,
                                    );
                                    if (!response?.status || response.status !== 200) {
                                        throw intl.formatMessage({ id: 'MESSAGES.SERVICE.VEHICLE_DELETE_ERROR' });
                                    }
                                }

                                //buscar na solicitação e reescrever
                                const tempRequest = _.cloneDeep(request);
                                const templicencaVeiculos = json.licencaVeiculos;
                                let key = _.findIndex(templicencaVeiculos, ['cdVeiculo', cdVeiculo]);
                                if (key >= 0) {
                                    templicencaVeiculos.splice(key, 1);
                                    json.licencaVeiculos = templicencaVeiculos;
                                    json = JSON.stringify(json);
                                    tempRequest.jsonSolicitacao = json;
                                    await licenseEndpoint.saveRequest(
                                        preparedFormData({ payload: tempRequest }),
                                        EXTERNAL_CONTEXT,
                                    );
                                    await dispatch(updateRequestDetails(tempRequest));
                                    temp = verifyLicencaNameAndFormat({ filteredCollectionData, index });
                                    dispatch(hideLoading());
                                }
                            } catch (error) {
                                dispatch(hideLoading());
                                notification(error, {
                                    toastId: 'SERVER_ERROR_INT',
                                    type: 'error',
                                });
                                setLoading(false);
                                return;
                            }
                        }
                    }
                } else {
                    temp = verifyLicencaNameAndFormat({
                        filteredCollectionData,
                        index,
                        cdLicencaName: 'cdLicencaVeiculo',
                    });
                    dispatch(hideLoading());
                }
            }
        }
        setData(temp);
    };

    const editElement = (element, index) => {
        setVehicleSelected({
            index,
            content: element?.veiculo,
        });
        setOpenDrawer(true);
    };

    const handleClickOpen = (index) => {
        setVehicleToAdd(index);
        setOpen(true);
    };

    const handleCancel = () => {
        setVehicleToAdd(null);
        setOpen(false);
    };

    const onChangeField = async (vehicle) => {
        if (service.cdServico === SERVICES_CODES.LICENCA_SEGUNDA_VIA) {
            if (oldVehicles.length === data.length) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.NOT_ADD_ELEMENTS_MORE_THAN_ORIGIAL' }), {
                    toastId: 'NOT_ADD_ELEMENTS_MORE_THAN_ORIGIAL',
                    type: 'warning',
                });
                return;
            }
        }

        setLoading(true);
        let temp = _.cloneDeep(data);
        const { dataBoExcluido, dataBoNotExcluido } = filteredCollectionData;
        const indexLastVehicle = dataBoNotExcluido.length - 1;
        const found = _.find(inspectedVehicles, ['cdVeiculo', Number(vehicle.cdVeiculo)]);
        const foundOnList = _.find(dataBoNotExcluido, ['cdVeiculo', Number(vehicle.cdVeiculo)]);

        if (found) {
            if (foundOnList) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.ELEMENT_ALREADY_EXISTS' }), {
                    toastId: 'ELEMENT_ALREADY_EXISTS',
                    type: 'warning',
                });
                setLoading(false);
                return;
            }
            const vehi = _.cloneDeep(found);
            if (vehi.documentos) {
                vehi.documentos = vehi.documentos.filter(
                    (doc) =>
                        doc.cdTipoDocumento === DOCUMENT_TYPES.CRLV ||
                        doc.cdTipoDocumento === DOCUMENT_TYPES.CERTIFICADO_VISTORIA,
                );

                const tempDoc = [];
                for (const doc of vehi.documentos) {
                    const temp = { ...doc };
                    if (!doc.fileName && doc.idFile) {
                        const response = await filesEndpoint.getFileData(doc.idFile);
                        if (response.status === 200 && response.data) {
                            temp.fileName = response.data.name;
                        }
                    }
                    const found = _.find(tempDoc, ['cdTipoDocumento', temp.cdTipoDocumento]);
                    if (!found) tempDoc.push(temp);
                }
                vehi.documentos = tempDoc;
            }

            dataBoNotExcluido[indexLastVehicle + 1] = {
                cdVeiculo: vehi.cdVeiculo,
                isOk: vehi.documentos.length > 0,
                veiculo: vehi,
            };
        }

        if (errorsList && errorsList.vehicles) {
            const errorsTemp = { ...errorsList };
            delete errorsTemp.vehicles;

            setErrorsList(errorsTemp);
        }

        temp = [...dataBoNotExcluido, ...dataBoExcluido];

        setLoading(false);
        setData(temp);
        handleCancel();
    };

    const onSaveRow = async (values, index) => {
        const valuesInt = { ...values };
        const docsExtrasValidation = validateDocumentsDescription(otherDocuments);
        if (!docsExtrasValidation && !isInternalForm) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.DOCUMENT_DESCRIPTION_REQUIRED' }), {
                toastId: 'MESSAGES.SERVICE.FORMS.DOCUMENT_DESCRIPTION_REQUIRED',
                type: 'error',
            });
            return;
        }

        if (otherDocuments && otherDocuments.length > 0) {
            const documentsValues = _.cloneDeep(valuesInt.documentos);
            otherDocuments.forEach((doc) => {
                let key = _.findIndex(valuesInt.documentos, (d) => {
                    return d.idFile === doc.idFile;
                });

                if (key < 0) {
                    documentsValues.push(doc);
                } else {
                    documentsValues[key] = doc;
                }
            });
            valuesInt.documentos = documentsValues;
        }

        if (index != null) {
            const temp = _.cloneDeep(data);
            if (data[index]) {
                temp[index] = { ...data[index] };
            }
            temp[index] = {
                ...temp[index],
                cdVeiculo: valuesInt.cdVeiculo,
                veiculo: valuesInt,
            };

            if (temp[index].veiculo.documentos && temp[index].veiculo.documentos.length > 0) {
                temp[index].isOk = true;
            }

            setData(temp);
        }

        onCancel();
    };

    const onCancel = () => {
        dispatch(clearFiles());
        setOpenDrawer();
        setVehicleSelected({});
    };

    return (
        <>
            <GenericDrawer open={openDrawer} title="Cadastro de Veículo" onClose={() => onCancel()}>
                <Container>
                    <VehicleForm
                        {...props}
                        classes={classes}
                        vehicle={vehicleSelected}
                        onSave={onSaveRow}
                        onCancel={onCancel}
                    />
                </Container>
            </GenericDrawer>

            <DialogVehicleSelection
                {...props}
                inspectedVehicles={inspectedVehicles}
                selectedVehicle={vehicleToAdd}
                open={open}
                handleCancel={handleCancel}
                handleSave={onChangeField}
                classes={classes}
                loading={loading}
            />

            <Grid item xs={12} className={classes.reponsiveTable}>
                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                    <h3 className={classes.titleTable} style={{ float: 'left' }}>
                        Veículos
                    </h3>
                    {!!maxVehicles && (
                        <Box>
                            <span>* Número máximo de veículos nesta solicitação: {maxVehicles}</span>
                        </Box>
                    )}
                </Box>
                <Table className={errorsList?.vehicles ? classes.tableError : classes.table}>
                    <TableHead>
                        <TableRow className={classes.tableRow}>
                            <TableCell className={classes.headTableCell} align="left">
                                ID
                            </TableCell>
                            <TableCell className={classes.headTableCell} align="left">
                                Placa
                            </TableCell>
                            <TableCell className={classes.headTableCell} align="left">
                                Nome do proprietário
                            </TableCell>
                            <TableCell className={classes.headTableCell} align="left">
                                CPF/CNPJ
                            </TableCell>
                            <TableCell className={classes.headTableCell} align="left">
                                Tipo de veículo
                            </TableCell>
                            <TableCell className={classes.headTableCell} align="left">
                                Capacidade
                            </TableCell>
                            <TableCell className={classes.headTableCell} align="left">
                                Categoria
                            </TableCell>
                            <TableCell className={classes.headTableCell} align="center" style={{ minWidth: '140px' }}>
                                {canEdit &&
                                    !hideAddButton &&
                                    (!service.boGeraDae ||
                                        maxVehicles > filteredCollectionData.dataBoNotExcluido.length) && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disableElevation
                                            onClick={() => changeElement()}
                                            className={classes.addButton}>
                                            <AddIcon /> Adicionar
                                        </Button>
                                    )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCollectionData.dataBoNotExcluido.map((row, indexRow) => {
                            let vehicleRow = row.veiculo;

                            if (!vehicleRow) {
                                vehicleRow = _.find(inspectedVehicles, ['cdVeiculo', row.cdVeiculo]);
                            }

                            let category = '';
                            let type = '';

                            if (vehicleRow?.cdCategoriaVeiculo) {
                                const found = _.find(vehicleCategories, [
                                    'cdCategoriaVeiculo',
                                    vehicleRow?.cdCategoriaVeiculo,
                                ]);
                                if (found) category = found.deCategoriaVeiculo;
                            }

                            if (vehicleRow?.cdTipoVeiculo) {
                                const found = _.find(vehicleType, ['cdTipoVeiculo', vehicleRow?.cdTipoVeiculo]);
                                if (found) type = found.deTipoVeiculo;
                            }

                            return (
                                <TableRow key={`irr-row-${indexRow}`} className={classes.tableRow}>
                                    <TableCell align="left">{row.cdVeiculo || ''}</TableCell>
                                    <TableCell align="left">{vehicleRow?.dePlaca || ''}</TableCell>
                                    <TableCell align="left">{vehicleRow?.nmProprietario || ''}</TableCell>
                                    <TableCell align="left">
                                        {vehicleRow?.deCpfCnpjProprietario
                                            ? helpers.formatarDocumento(vehicleRow?.deCpfCnpjProprietario)
                                            : ''}
                                    </TableCell>
                                    <TableCell align="left">{type}</TableCell>
                                    <TableCell align="left">{vehicleRow?.qtCapacidade || ''}</TableCell>
                                    <TableCell align="left">{category}</TableCell>

                                    <TableCell align="right">
                                        {canEdit && row.isOk && (
                                            <Button disabled className={classes.btnSigleAction} size="small">
                                                <CheckIcon fontSize="inherit" />
                                            </Button>
                                        )}

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => editElement(row, indexRow)}
                                            className={classes.btnSigleAction}
                                            size="small">
                                            {canEdit && !isInternalForm ? (
                                                <EditIcon fontSize="inherit" />
                                            ) : (
                                                <AssignmentIcon fontSize="inherit" />
                                            )}
                                        </Button>
                                        {showDeleteButton && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => changeElement(indexRow)}
                                                className={classes.btnSigleAction}
                                                size="small">
                                                <DeleteIcon fontSize="inherit" />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Grid>
        </>
    );
}

export default injectIntl(VehicleTableAdd);
